// ============================================================
// Submissão de leads — N8N webhook + VisitorAPI + SHA256 (Meta)
// ============================================================

const N8N_WEBHOOK_URL =
  (import.meta.env.VITE_N8N_WEBHOOK_URL as string | undefined) ||
  'https://n8n.srv971270.hstgr.cloud/webhook/inscricao-evento';

// Endpoint da Vercel Function — quando o frontend roda em Hostinger,
// precisamos chamar a API direto na Vercel (CORS já liberado pra giovannievento.com).
// VITE_API_BASE pode sobrescrever via env (.env.production) se trocar de domínio.
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ||
  'https://do-zero-ao-evento-americano-antigo.vercel.app';

const SHEETS_INSCRICAO_URL = `${API_BASE}/api/sheets-inscricao`;
const PESQUISA_SUBMIT_URL = `${API_BASE}/api/pesquisa-submit`;

const IPINFO_TOKEN =
  (import.meta.env.VITE_IPINFO_TOKEN as string | undefined) ||
  '71718cb3c2b3ae';

// ---------- SHA256 (WebCrypto, browser-native) ----------
const sha256 = async (input: string): Promise<string> => {
  const normalized = input.trim().toLowerCase();
  const buf = new TextEncoder().encode(normalized);
  const hashBuf = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hashBuf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

// ---------- IPinfo ----------
type Geo = {
  ip: string;
  state: string;
  city: string;
  neighborhood: string;
  postal: string;
  latitude: string;
  longitude: string;
  country: string;
  timezone: string;
  isp: string;
  asn: string;
  hostname: string;
  connectionType: 'mobile' | 'broadband' | 'satellite' | 'unknown';
};

// ISPs/ASNs bloqueados (IP não será enviado — rede não representa localização real do usuário)
const BLOCKED_PATTERNS = [
  /starlink/i,
  /space exploration/i,
  /AS14593/i, // SpaceX Starlink ASN
];

// ASNs de redes móveis brasileiras conhecidas (4G/5G)
const MOBILE_ASNS = new Set([
  'AS22085', // Claro S/A (móvel)
  'AS26599', // TIM S.A.
  'AS18881', // Vivo/Telefônica Móvel
  'AS28573', // Claro NXT — pode ser móvel OU NET; desambiguamos via hostname
]);

// Classifica via hostname (mais preciso) e ASN (fallback)
const classifyConnection = (
  hostname: string,
  asn: string,
  org: string,
): Geo['connectionType'] => {
  const h = hostname.toLowerCase();
  const o = org.toLowerCase();

  // Satélite
  if (/starlink|space exploration|hughesnet|viasat/.test(o)) return 'satellite';

  // Hostname com marcador móvel inequívoco
  if (/\b(3g|4g|5g|lte|gprs|wireless|mobile|mbb)\b/.test(h)) return 'mobile';

  // Hostname com marcador de banda larga inequívoco
  if (/virtua|vivo\.com\.br|gvt|oi\.net\.br|fibra|ftth|cable|dsl|adsl|pppoe/.test(h)) return 'broadband';

  // Fallback por ASN
  if (MOBILE_ASNS.has(asn)) return 'mobile';

  // Fallback por nome da org
  if (/fibra|net\b|virtua|velox|gvt|brisanet|unifique|sumicity/.test(o)) return 'broadband';
  if (/(vivo|claro|tim|oi|algar|nextel)\b/.test(o) && !/nxt|net|virtua/.test(o)) return 'mobile';

  return 'unknown';
};

const fetchGeo = async (): Promise<Geo> => {
  const empty: Geo = {
    ip: '', state: '', city: '', neighborhood: '',
    postal: '', latitude: '', longitude: '', country: '', timezone: '',
    isp: '', asn: '', hostname: '', connectionType: 'unknown',
  };
  if (!IPINFO_TOKEN) return empty;
  try {
    const res = await fetch(`https://ipinfo.io/json?token=${IPINFO_TOKEN}`);
    if (!res.ok) return empty;
    const data = await res.json();
    // org vem no formato "AS22085 Claro S/A" no plano free
    const org: string = data.org || '';
    const asnMatch = org.match(/^(AS\d+)\s+(.*)$/);
    const asn = asnMatch?.[1] || '';
    const isp = asnMatch?.[2] || org;
    const hostname: string = data.hostname || '';
    const connectionType = classifyConnection(hostname, asn, isp);
    const isBlocked = BLOCKED_PATTERNS.some(p => p.test(isp) || p.test(asn));
    const [lat, lng] = (data.loc || '').split(',');
    return {
      ip: isBlocked ? '' : (data.ip || ''),
      state: data.region || '',
      city: data.city || '',
      neighborhood: '', // IPinfo não retorna bairro
      postal: data.postal || '',
      latitude: lat || '',
      longitude: lng || '',
      country: data.country || '',
      timezone: data.timezone || '',
      isp,
      asn,
      hostname,
      connectionType,
    };
  } catch {
    return empty;
  }
};

// ---------- Tracking params (URL → payload → /obrigado) ----------
const TRACKING_FIELDS = ['conta', 'um', 'dois', 'tres', 'quatro', 'cinco'] as const;
type TrackingField = typeof TRACKING_FIELDS[number];
type TrackingParams = Partial<Record<TrackingField, string>>;

// Lê parâmetros da URL atual e persiste em sessionStorage (fallback se o usuário recarregar).
export const captureTrackingParams = (): TrackingParams => {
  const params = new URLSearchParams(window.location.search);
  const out: TrackingParams = {};
  for (const field of TRACKING_FIELDS) {
    const fromUrl = params.get(field);
    const fromStorage = (() => { try { return sessionStorage.getItem(`track_${field}`); } catch { return null; } })();
    const value = (fromUrl ?? fromStorage ?? '').trim();
    if (value) {
      out[field] = value;
      try { sessionStorage.setItem(`track_${field}`, value); } catch {}
    }
  }
  return out;
};

// ---------- Payload ----------
export type LeadPayload = {
  formName: string;
  name?: string;
  email: string;
  phone?: string;        // telefone fixo (opcional)
  whatsapp: string;      // com DDI, ex: "+5511999999999"
  countryName?: string;  // nome do país detectado, ex: "Brasil"
  nationalNumber?: string; // número sem DDI, ex: "11999999999"
};

// Tudo o que enriquecemos do lead (geo + hashes + tracking + metadata)
// — útil pra empurrar pro dataLayer (GTM) depois do submit.
export type EnrichedLead = {
  formName: string;
  datetime: string;
  name: string;
  email: string;
  phone: string;
  whatsapp: string;
  email_sha256: string;
  phone_sha256: string;
  whatsapp_sha256: string;
  ip: string;
  state: string;
  city: string;
  neighborhood: string;
  postal: string;
  latitude: string;
  longitude: string;
  country: string;
  timezone: string;
  isp: string;
  asn: string;
  hostname: string;
  connectionType: 'mobile' | 'broadband' | 'satellite' | 'unknown';
  userAgent: string;
  referrer: string;
  pageUrl: string;
  conta: string;
  um: string;
  dois: string;
  tres: string;
  quatro: string;
  cinco: string;
  telefone_pais_detectado: string;
  telefone_numero_nacional: string;
};

export const submitLead = async (lead: LeadPayload): Promise<EnrichedLead> => {
  const tracking = captureTrackingParams();
  const cleanWhatsapp = lead.whatsapp.replace(/\D/g, '');
  const fullWhatsapp = lead.whatsapp.startsWith('+') ? lead.whatsapp : `+${cleanWhatsapp}`;
  const phoneForHash = lead.phone ? lead.phone.replace(/\D/g, '') : cleanWhatsapp;

  const [emailHash, phoneHash, whatsappHash, geo] = await Promise.all([
    sha256(lead.email),
    sha256(phoneForHash),
    sha256(cleanWhatsapp),
    fetchGeo(),
  ]);

  const payload: EnrichedLead = {
    formName: lead.formName,
    datetime: new Date().toISOString(),
    // Raw — para planilha / CRM
    name: lead.name || '',
    email: lead.email,
    phone: fullWhatsapp,
    whatsapp: fullWhatsapp,
    // Hash SHA256 — para Meta CAPI (conforme termos da Meta)
    email_sha256: emailHash,
    phone_sha256: phoneHash,
    whatsapp_sha256: whatsappHash,
    // Geolocalização (IP enviado em texto claro — permitido pela Meta)
    ip: geo.ip,
    state: geo.state,
    city: geo.city,
    neighborhood: geo.neighborhood,
    postal: geo.postal,
    latitude: geo.latitude,
    longitude: geo.longitude,
    country: geo.country,
    timezone: geo.timezone,
    isp: geo.isp,
    asn: geo.asn,
    hostname: geo.hostname,
    connectionType: geo.connectionType,
    // Metadados úteis
    userAgent: navigator.userAgent,
    referrer: document.referrer || '',
    pageUrl: window.location.href,
    // Campos de tracking (URL params)
    conta: tracking.conta || '',
    um: tracking.um || '',
    dois: tracking.dois || '',
    tres: tracking.tres || '',
    quatro: tracking.quatro || '',
    cinco: tracking.cinco || '',
    telefone_pais_detectado: lead.countryName || '',
    telefone_numero_nacional: lead.nationalNumber || cleanWhatsapp,
  };

  // Dispara n8n + Sheets (Vercel Function) em PARALELO — redundância
  // Se um falhar, o outro ainda grava
  const n8nCall = fetch(N8N_WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).then(async (res) => {
    if (!res.ok) {
      console.error('[submit] n8n respondeu', res.status, await res.text().catch(() => ''));
    }
    return res;
  });

  const sheetsCall = fetch(SHEETS_INSCRICAO_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).then(async (res) => {
    if (!res.ok) {
      console.error('[submit] sheets respondeu', res.status, await res.text().catch(() => ''));
    }
    return res;
  });

  const results = await Promise.allSettled([n8nCall, sheetsCall]);
  const n8nOk = results[0].status === 'fulfilled';
  const sheetsOk = results[1].status === 'fulfilled';

  if (results[0].status === 'rejected') {
    console.error('[submit] n8n falhou', results[0].reason);
    // Fallback sendBeacon pro n8n (caso user já navegou)
    try {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(N8N_WEBHOOK_URL, blob);
    } catch {}
  }
  if (results[1].status === 'rejected') {
    console.error('[submit] sheets falhou', results[1].reason);
  }

  // Só joga erro se AMBOS falharem (impede UI de avançar sem nenhuma gravação)
  if (!n8nOk && !sheetsOk) {
    throw new Error('Ambos os destinos falharam (n8n + Sheets)');
  }

  // Retorna o payload completo pra quem chamou poder empurrar no dataLayer (GTM)
  return payload;
};

// ============================================================
// Geolocalização precisa (browser API + reverse geocoding)
// Usado na /obrigado pra enriquecer o lead com bairro/rua/CEP reais
// ============================================================
export type PreciseGeo = {
  source: 'browser';
  latitude: number;
  longitude: number;
  accuracy: number;
  road: string;
  suburb: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
};

const reverseGeocode = async (
  lat: number,
  lng: number,
): Promise<Omit<PreciseGeo, 'source' | 'latitude' | 'longitude' | 'accuracy'>> => {
  const empty = { road: '', suburb: '', city: '', state: '', postcode: '', country: '' };
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=pt-BR`;
    const res = await fetch(url, { signal: AbortSignal.timeout(6000) });
    if (!res.ok) return empty;
    const data = await res.json();
    const a = data.address || {};
    return {
      road: a.road || a.pedestrian || a.path || '',
      suburb: a.suburb || a.neighbourhood || a.quarter || a.city_district || '',
      city: a.city || a.town || a.village || a.municipality || '',
      state: a.state || a.region || '',
      postcode: a.postcode || '',
      country: a.country || '',
    };
  } catch {
    return empty;
  }
};

export const requestPreciseGeo = (): Promise<PreciseGeo | null> => {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const addr = await reverseGeocode(latitude, longitude);
        resolve({ source: 'browser', latitude, longitude, accuracy, ...addr });
      },
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  });
};

// ============================================================
// Submissão da PESQUISA (página /obrigado)
// Usa email + whatsapp do sessionStorage pra identificar o lead
// ============================================================
export type PesquisaPayload = {
  answers: Record<string, string | string[]>;
  preciseGeo?: PreciseGeo | null;
};

export const submitPesquisa = async (pesquisa: PesquisaPayload): Promise<void> => {
  const urlParams = new URLSearchParams(window.location.search);
  const getIdentity = (urlKey: string, storageKey: string) => {
    const fromUrl = (urlParams.get(urlKey) || '').trim();
    if (fromUrl) return fromUrl;
    try { return (sessionStorage.getItem(storageKey) || '').trim(); } catch { return ''; }
  };
  const email = getIdentity('email', 'lead_email');
  const whatsapp = getIdentity('phone', 'lead_whatsapp');
  const tracking = captureTrackingParams();

  const [emailHash, whatsappHash] = await Promise.all([
    email ? sha256(email) : Promise.resolve(''),
    whatsapp ? sha256(whatsapp.replace(/\D/g, '')) : Promise.resolve(''),
  ]);

  const pg = pesquisa.preciseGeo || null;
  const payload = {
    formName: 'Pesquisa - Do Zero ao Mercado Americano',
    datetime: new Date().toISOString(),
    email,
    whatsapp,
    email_sha256: emailHash,
    whatsapp_sha256: whatsappHash,
    answers: pesquisa.answers,
    preciseGeoConsent: !!pg,
    preciseGeoSource: pg ? pg.source : '',
    preciseLatitude: pg ? String(pg.latitude) : '',
    preciseLongitude: pg ? String(pg.longitude) : '',
    preciseAccuracy: pg ? String(pg.accuracy) : '',
    preciseRoad: pg ? pg.road : '',
    preciseSuburb: pg ? pg.suburb : '',
    preciseCity: pg ? pg.city : '',
    preciseState: pg ? pg.state : '',
    precisePostcode: pg ? pg.postcode : '',
    preciseCountry: pg ? pg.country : '',
    userAgent: navigator.userAgent,
    referrer: document.referrer || '',
    pageUrl: window.location.href,
    conta: tracking.conta || '',
    um: tracking.um || '',
    dois: tracking.dois || '',
    tres: tracking.tres || '',
    quatro: tracking.quatro || '',
    cinco: tracking.cinco || '',
  };

  const res = await fetch(PESQUISA_SUBMIT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  });

  if (!res.ok) {
    console.error('[pesquisa] vercel respondeu', res.status, await res.text().catch(() => ''));
  }
};
